/**
 * 认证上下文（S04）。
 *
 * v3 auth 是 Supabase 风格：getSession() 为守卫标准，onAuthStateChange 做响应式订阅。
 * 登录方式：邮箱验证码（signInWithOtp → data.verifyOtp）。
 * 匿名登录保留为开发逃生口（新环境默认关闭，失败会被捕获并提示）。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { auth } from "./cloudbase";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyAuth = any;

export type AuthStatus = "loading" | "authed" | "anon";

interface AuthUser {
  id?: string;
  email?: string;
  nickname?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  raw?: any;
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** 发送邮箱验证码。返回是否成功（失败已 toast 由调用方处理 error）。 */
  sendEmailCode: (email: string) => Promise<void>;
  /** 用验证码完成登录。 */
  verifyEmailCode: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** 开发逃生口：匿名登录（控制台未开启时会报错）。 */
  signInAnonymouslyDev: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// verifyOtp 回调需要保存在 signInWithOtp 与输入验证码之间
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pendingVerify: any = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  const loadUser = useCallback(async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (auth as AnyAuth).getSession();
      if (error || !data?.session) {
        setStatus("anon");
        setUser(null);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: userData } = await (auth as AnyAuth).getUser();
      const u = userData?.user ?? {};
      // 匿名会话在本环境调不了云函数（网关 403 EXCEED_AUTHORITY），当作未登录清掉，
      // 免得用户以为自己已登录却处处失败。
      if (u.is_anonymous === true || u.isAnonymous === true || data.session.user?.is_anonymous === true) {
        await (auth as AnyAuth).signOut();
        setStatus("anon");
        setUser(null);
        return;
      }
      setStatus("authed");
      setUser({
        id: u.id,
        email: u.email,
        nickname: u.user_metadata?.nickName ?? u.user_metadata?.nickname,
        raw: u,
      });
    } catch {
      setStatus("anon");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void loadUser();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = (auth as AnyAuth).onAuthStateChange(() => {
      void loadUser();
    });
    return () => data?.subscription?.unsubscribe?.();
  }, [loadUser]);

  const sendEmailCode = useCallback(async (email: string) => {
    const { error } = await (auth as AnyAuth).signInWithOtp({ email });
    if (error) throw error;
  }, []);

  const verifyEmailCode = useCallback(async (_email: string, token: string) => {
    if (!pendingVerify) {
      throw new Error("请先获取验证码");
    }
    const { error } = await pendingVerify.verifyOtp({ token });
    if (error) throw error;
    pendingVerify = null;
    await loadUser();
  }, [loadUser]);

  const signOut = useCallback(async () => {
    await (auth as AnyAuth).signOut();
    await loadUser();
  }, [loadUser]);

  const signInAnonymouslyDev = useCallback(async () => {
    await (auth as AnyAuth).signInAnonymously();
    await loadUser();
  }, [loadUser]);

  // 拦截 signInWithOtp 的返回以保存 verifyOtp 回调
  const sendEmailCodeWrapped = useCallback(
    async (email: string) => {
      const res = await (auth as AnyAuth).signInWithOtp({ email });
      pendingVerify = res?.data ?? null;
      if (res?.error) throw res.error;
    },
    []
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      sendEmailCode: sendEmailCodeWrapped,
      verifyEmailCode,
      signOut,
      signInAnonymouslyDev,
      refresh: loadUser,
    }),
    [status, user, sendEmailCodeWrapped, verifyEmailCode, signOut, signInAnonymouslyDev, loadUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}
